import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  MapPin, 
  Spinner, 
  CheckCircle, 
  Warning,
  Truck
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientLocatePage() {
  const { token } = useParams();
  const [status, setStatus] = useState('initial'); // initial, loading, success, error, not_found
  const [error, setError] = useState('');
  const [intervention, setIntervention] = useState(null);

  useEffect(() => {
    // Verify token is valid
    const checkToken = async () => {
      try {
        const response = await axios.get(`${API}/api/locate/${token}`);
        setIntervention(response.data);
        if (response.data.has_location) {
          setStatus('already_shared');
        }
      } catch (err) {
        setStatus('not_found');
      }
    };
    checkToken();
  }, [token]);

  const shareLocation = async () => {
    setStatus('loading');
    setError('');

    if (!navigator.geolocation) {
      setError('La géolocalisation n\'est pas supportée par votre navigateur');
      setStatus('error');
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        });
      });

      const { latitude, longitude } = position.coords;

      // Reverse geocode using OpenStreetMap Nominatim
      let address = '';
      try {
        const geoResponse = await axios.get(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
          { headers: { 'Accept-Language': 'fr' } }
        );
        address = geoResponse.data.display_name;
      } catch (e) {
        // Continue without address
      }

      // Send to backend
      await axios.post(`${API}/api/locate/${token}`, {
        latitude,
        longitude,
        address
      });

      setStatus('success');
    } catch (err) {
      if (err.code === 1) {
        setError('Vous avez refusé l\'accès à votre position. Veuillez autoriser la géolocalisation.');
      } else if (err.code === 2) {
        setError('Impossible de déterminer votre position. Veuillez réessayer.');
      } else if (err.code === 3) {
        setError('Délai d\'attente dépassé. Veuillez réessayer.');
      } else {
        setError('Erreur lors de l\'envoi de votre position');
      }
      setStatus('error');
    }
  };

  if (status === 'not_found') {
    return (
      <PageContainer>
        <div className="text-center">
          <Warning size={64} className="mx-auto text-amber-500 mb-4" />
          <h1 className="text-2xl font-heading font-bold text-gray-900 mb-2">
            Lien invalide
          </h1>
          <p className="text-gray-500">
            Ce lien de géolocalisation n'est plus valide ou a expiré.
          </p>
        </div>
      </PageContainer>
    );
  }

  if (status === 'success' || status === 'already_shared') {
    return (
      <PageContainer>
        <div className="text-center">
          <CheckCircle size={64} weight="fill" className="mx-auto text-reco-500 mb-4" />
          <h1 className="text-2xl font-heading font-bold text-gray-900 mb-2">
            {status === 'already_shared' ? 'Position déjà transmise' : 'Position transmise !'}
          </h1>
          <p className="text-gray-500">
            {status === 'already_shared' 
              ? 'Votre position a déjà été partagée avec le technicien.'
              : 'Merci ! Votre position a été envoyée à notre technicien.'
            }
          </p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-reco-100 mb-6">
          <Truck size={40} weight="fill" className="text-reco-500" />
        </div>
        
        <h1 className="text-2xl font-heading font-bold text-gray-900 mb-2">
          <span className="text-reco-500">reco</span>pneus
        </h1>
        <p className="text-gray-500 mb-8">
          Partagez votre position pour faciliter l'intervention
        </p>

        {intervention && (
          <p className="text-sm text-gray-400 mb-6">
            Intervention pour: {intervention.client_name}
          </p>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm mb-6">
            <Warning size={18} />
            <span>{error}</span>
          </div>
        )}

        <Button
          onClick={shareLocation}
          disabled={status === 'loading'}
          className="w-full h-16 bg-reco-500 hover:bg-reco-600 text-white text-lg font-bold gap-3 rounded-full"
          data-testid="share-location-button"
        >
          {status === 'loading' ? (
            <>
              <Spinner size={24} className="animate-spin" />
              Récupération de la position...
            </>
          ) : (
            <>
              <MapPin size={24} weight="fill" />
              Appuyer pour partager ma position
            </>
          )}
        </Button>

        <p className="text-xs text-gray-400 mt-4">
          En appuyant sur ce bouton, vous autorisez le partage de votre position GPS avec RC PNEUS pour cette intervention uniquement.
        </p>
      </div>
    </PageContainer>
  );
}

function PageContainer({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-8 max-w-sm w-full">
        {children}
      </div>
    </div>
  );
}
